"use client";
import { FolderTreeItem } from "@zapier/client";
import {
  Table,
  useFolderAndZapsModalStates,
  useRouterParams,
} from "@zapier/shared";
import { FolderTypeQueryState } from "@zapier/utils";
import { FoldersOrder } from "@zapier/bff";
import FolderRow from "./FolderRow";
import { useShowEmptyFolderTable } from "./useShowEmptyFolderTable";
import { FolderModals } from "./FolderModals";
import { EmptyFolderTable } from "./EmptyFolderTable";
import styles from "./FolderTable.module.css";
import {
  BetaColumnHeader,
  SortDescriptor,
  SortDirection,
} from "@zapier/design-system";

interface FolderTableProps {
  ariaLabel: string;
  folders: FolderTreeItem[];
  folderType?: FolderTypeQueryState | null;
  isFoldersExpandedByDefault?: boolean;
  isLoading?: boolean;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  order?: FoldersOrder;
  searchQuery?: string | null;
}

const columns = [] as BetaColumnHeader[];

const FolderTable = ({
  ariaLabel,
  folders,
  folderType,
  isFoldersExpandedByDefault,
  isLoading,
  onSortChange,
  order,
  searchQuery,
}: FolderTableProps) => {
  const {
    currentFolder,
    setCurrentFolder,
    currentFolderModal,
    setCurrentFolderModal,
  } = useFolderAndZapsModalStates();
  const { owner: selectedOwner } = useRouterParams();

  // default sorting by title in ascending order
  const sortDescriptor = {
    column: "title",
    direction: "ascending" as SortDirection,
  };

  // update sortDescriptor if order is provided
  if (order) {
    sortDescriptor.column = order.replace("-", "");
    sortDescriptor.direction = (
      order[0] === "-" ? "descending" : "ascending"
    ) as SortDirection;
  }

  const emptyTableType = useShowEmptyFolderTable({
    sortDescriptor,
    order,
    searchQuery,
    hasFolders: folders.length > 0,
    isLoading: !!isLoading,
    selectedOwner,
  });

  if (emptyTableType) {
    return (
      <EmptyFolderTable
        tableType={emptyTableType}
        folderType={folderType}
        setCurrentFolderModal={setCurrentFolderModal}
        currentFolderModal={currentFolderModal}
      />
    );
  }

  return (
    <>
      <Table
        allowSortByTitle={true}
        ariaLabel={ariaLabel}
        className={styles.folderTable}
        columns={columns}
        isLoading={isLoading}
        onSortChange={onSortChange}
        showCheckboxes={false}
        showOwner={true}
        // No share action wired on these rows — don't reserve share-button
        // width so the Options column stays tight and consistent.
        showShareButton={false}
        sortDescriptor={sortDescriptor}
      >
        {folders.map((folder, index) => (
          <FolderRow
            folder={folder}
            folderLevel={0}
            isFoldersExpandedByDefault={isFoldersExpandedByDefault}
            key={index} // Use index as key as react-aria-components the ids of the rows change
            searchQuery={searchQuery}
            includeSelectionColumn={false}
            setCurrentModal={setCurrentFolderModal}
            setCurrentFolder={setCurrentFolder}
          />
        ))}
      </Table>

      <FolderModals
        currentModal={currentFolderModal}
        setCurrentModal={setCurrentFolderModal}
        currentFolder={currentFolder}
        setCurrentFolder={setCurrentFolder}
      />
    </>
  );
};
export default FolderTable;
